import { CellValueType, HeaderCell, Row as RowInterface } from '../Interface/Hook/Interest/InterestTableDTO';
import { Sort } from '../Interface/Table/Sort';
import { PropertyAttributes } from '../Interface/Response/Interest/PropertyAttributes';
import { Interest } from '../Interface/Response/Interest/Interest';
import { Cell as AcceptationGuideCell } from '../Interface/Response/Interest/AcceptationGuideData';
import { getSortableIndex, getSortableValue } from '../Helpers/Table/ProductPropertyHelper';
import { Value } from '../Enum/ProductProperties/Value';

interface SortableValue {
    value: string | number;
    sortIndex?: number;
}

const getFirstCellWithoutDecorator = (row: RowInterface): number => {
    for (let i = 0; i < row.cells.length; i++) {
        if (!row.cells[i].decorator) {
            return i;
        }
    }

    return 0;
};

const isProductProperty = (value: CellValueType): value is PropertyAttributes =>
    (value as PropertyAttributes).value !== undefined;

const isInterest = (value: CellValueType): value is Interest => (value as Interest).percentage !== undefined;

const isAcceptationGuideCell = (value: CellValueType): value is AcceptationGuideCell =>
    (value as AcceptationGuideCell).label !== undefined;

const compareValue = (a: string | number, b: string | number, ascending: boolean, value: Value): number => {
    if (a === value as string && b !== value as string) {
        return ascending ? 1 : -1;
    }

    if (a !== value as string && b === value as string) {
        return ascending ? -1 : 1;
    }

    return 0;
};

const compareIndex = (a: number | undefined, b: number | undefined, ascending: boolean): number => {
    if (a === b) {
        return 0;
    }

    if (a === undefined && b !== undefined) {
        return ascending ? -1 : 1;
    }

    if (a !== undefined && b === undefined) {
        return ascending ? 1 : -1;
    }

    const comparison = (a as number) < (b as number) ? 1 : -1;
    return ascending ? comparison * -1 : comparison;
}

const compare = (a: SortableValue, b: SortableValue, ascending: boolean): number => {
    const unknownCompare = compareValue(a.value, b.value, ascending, Value.UNKNOWN);
    if (unknownCompare !== 0) {
        return unknownCompare;
    }

    const inapplicableCompare = compareValue(a.value, b.value, ascending, Value.INAPPLICABLE);
    if (inapplicableCompare !== 0) {
        return inapplicableCompare;
    }

    const indexCompare = compareIndex(a.sortIndex, b.sortIndex, ascending);
    if (indexCompare !== 0) {
        return indexCompare;
    }

    const isStringA = a.value as string === '' ? true : isNaN(a.value as number);
    const isStringB = b.value as string === '' ? true : isNaN(b.value as number);

    if (isStringA && isStringB) {
        return (a.value as string).localeCompare(b.value as string);
    }

    if (isStringA && !isStringB) {
        return ascending ? 1 : -1;
    }

    if (!isStringA && isStringB) {
        return ascending ? -1 : 1;
    }

    return Math.sign(parseFloat(a.value as string) - parseFloat(b.value as string));
}

const getValue = (row: RowInterface, columnNo: number): SortableValue => {
    const value = row.cells[columnNo].value;
    if (isProductProperty(value)) {
        return {
            value: getSortableValue(value),
            sortIndex: getSortableIndex(value),
        };
    }

    if (isInterest(value)) {
        return { value: (value.percentage ?? 0) as string };
    }

    if (isAcceptationGuideCell(value)) {
        return { value: value.label };
    }

    return { value: value as string };
}

const compareRows = (
    rowA: RowInterface,
    rowB: RowInterface,
    columnNo: number,
    secondColumnNo: number,
    ascending: boolean,
): number => {
    // TODO Improve sorting by introducing a sort value on the different interfaces ()
    const valueAColumnNo = getValue(rowA, columnNo);
    const valueBColumnNo = getValue(rowB, columnNo);
    const valueASecondColumnNo = getValue(rowA, secondColumnNo);
    const valueBSecondColumnNo = getValue(rowB, secondColumnNo);

    if (valueAColumnNo === valueBColumnNo) {
        return ascending
            ? compare(valueBSecondColumnNo, valueASecondColumnNo, ascending)
            : compare(valueASecondColumnNo, valueBSecondColumnNo, ascending);
    }

    return ascending
        ? compare((valueBColumnNo) ?? 'zzz', (valueAColumnNo) ?? 'zzz', ascending)
        : compare((valueAColumnNo) ?? '', (valueBColumnNo) ?? '', ascending);
};

const getSortByColumnNo = (headers: HeaderCell[], sort: Sort): number =>
    headers.findIndex(({ prop }) => prop === sort.prop);

const sortData = (
    data: RowInterface[],
    columnNo: number,
    secondColumnNo: number,
    ascending: boolean,
): RowInterface[] => {
    return data.sort((a, b) => compareRows(b, a, columnNo, secondColumnNo, ascending));
};

const sortTableData = (data: RowInterface[], headers: HeaderCell[], sort?: Sort): RowInterface[] => {
    if (!sort || !sort.prop) {
        return data;
    }

    const sortByColumnNo = getSortByColumnNo(headers, sort);
    if (sortByColumnNo > 0) {
        return sortData(data, sortByColumnNo, getFirstCellWithoutDecorator(data[0]), sort.ascending);
    }

    return data;
};

export default sortTableData;
